import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import Swal, { SweetAlertIcon } from 'sweetalert2';

import { DurationService } from 'src/app/services/duration.service';
import { Duration, Licence, Profile, Role, Template, TemplateProfile } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';

import { ColumnsFilterModalComponent } from '../columns-filter-modal/columns-filter-modal.component';

export enum State {
  active,
  expired,
  disabled,
  noperms
}

export interface EntryExpiry {
  text: string;
  date?: Date;
  duration?: Duration;
  licence: boolean;
}

export interface Entry extends Profile, Licence {
  prf: Profile | TemplateProfile;
  name?: string;
  fullname?: string;
  roles?: RoleSource[];
  expiry?: EntryExpiry;
  rolesTooltip?: string;
  labelsText?: string;
  state?: State;
  patternTooltip?: string;
}

export interface Columns {
  uuid?: boolean;
  username?: boolean;
  pattern?: boolean;
  licence?: boolean;
  usage?: boolean;
  activations?: boolean;
  fullname?: boolean;
  labels?: boolean;
  mail?: boolean;
  roles?: boolean;
  registered?: boolean;
  created?: boolean;
  expiry?: boolean;
}

export interface RoleSource {
  role?: string;
  expiration?: Date;
  licences?: string[];
}

@Component({
  selector: 'user-page-view',
  templateUrl: './user-page-view.component.html',
  styleUrls: ['./user-page-view.component.scss']
})
export class UserPageViewComponent implements OnInit, OnDestroy, OnChanges {
  // Input/Output

  @Input() public entries: Entry[];
  @Input() public roles: Role[];
  @Input() public columns: Columns = {};
  @Input() public username = true;
  @Input() public pattern = false;
  @Input() public clearSelection: Observable<void>;
  @Input() public canDelete: boolean;
  @Input() public canEdit: boolean;

  @Output() public onSelect = new EventEmitter<any>();
  @Output() public onUpdate = new EventEmitter<any>();
  @Output() public onUpdated = new EventEmitter<any>();
  @Output() public onDelete = new EventEmitter<any>();
  @Output() public onUpdateMulti = new EventEmitter<any[]>();
  @Output() public onDeleteMulti = new EventEmitter<any[]>();

  // Template Properties

  public filter = { username: '', mail: '', role: '*', licence: '*', expiry: 'any', labelKey: '*', labelValue: '*' };
  public count: { total: number; active: number; expired: number; disabled: number };
  public filtered: Entry[];
  public sorted: Entry[];
  public page = 1;
  public pageSize = 100;
  public licences: string[];
  public selection = {};
  public sortingKey: string = null;
  public sortingReverse = false;
  public allSelected = false;
  public dateFormat = environment.dateFormat;
  public State = State;

  // Subscription

  private clearSelectionSubscription: Subscription;

  // Label lookups for filter dropdowns

  public labelKeys: string[] = null;
  public labelValues: { [key: string]: string[] } = null;

  public get filterLabelKey(): string {
    return this.filter.labelKey;
  }

  public set filterLabelKey(val: string) {
    this.filter.labelKey = val;
    this.filter.labelValue = '*';
  }

  public get selectedCount() {
    return this.sorted.filter(u => this.selection[u.uuid]).length;
  }

  // Constants & Lookups

  public readonly licenceUsageInfo = UserService.licenceUsageInfo;

  public readonly expiryFilters = {
    any: (e: Entry) => true,
    active: (e: Entry) => e.state === State.active,
    disabled: (e: Entry) => e.state === State.disabled || e.state === State.noperms,
    expired: (e: Entry) => e.state === State.expired
  };

  private readonly swalBase = {
    icon: 'question' as SweetAlertIcon,
    showCancelButton: true,
    buttonsStyling: false,
    confirmButtonText: 'Yes, delete it!',
    customClass: {
      confirmButton: 'btn btn-primary me-1',
      cancelButton: 'btn btn-danger'
    }
  };

  private readonly sortingOrder: { [key: string]: (a: Entry, b: Entry) => number } = {
    uuid: (a, b) => this.compareString(a.uuid, b.uuid),
    username: (a, b) => this.compareString(a.username ?? a.pattern, b.username ?? b.pattern),
    fullname: (a, b) => this.compareString(a.fullname, b.fullname),
    mail: (a, b) => this.compareString(a.prf.meta.mail, b.prf.meta.mail),
    roles: (a, b) => this.compareRoles(a.prf, b.prf),
    licence: (a, b) => this.compareString(a.key, b.key),
    activations: (a, b) => (a.activations ?? 0) - (b.activations ?? 0),
    registered: (a, b) => this.compareDate(a.prf.meta?.registration, b.prf.meta?.registration),
    created: (a, b) => this.compareDate(a.created, b.created),
    expiry: (a, b) => this.compareExpiry(a, b)
  };

  constructor(public duration: DurationService, private modal: NgbModal) {}

  public ngOnInit(): void {
    if (this.clearSelection) {
      this.clearSelectionSubscription = this.clearSelection.subscribe(() => this.selectAll(false));
    }
    this.updateFiltered();
  }

  public ngOnDestroy(): void {
    this.clearSelectionSubscription?.unsubscribe();
  }

  public ngOnChanges(): void {
    this.setupEntries();
    this.setupLabelLookup();
    this.updateFiltered();
  }

  public showColumFilterModal(): void {
    const modalRef = this.modal.open(ColumnsFilterModalComponent, { size: 'md' });
    modalRef.componentInstance.columns = this.columns;
  }

  // Selection, Update & Delete

  public select(uuid: string, value: boolean): void {
    if (!value) {
      this.allSelected = false;
    }
    this.selection[uuid] = value;
    this.onSelect.emit(this.selection);
  }

  public selectAll(value): void {
    this.allSelected = value;
    for (const e of this.sorted) {
      this.selection[e.uuid] = value;
    }
    this.onSelect.emit(this.selection);
  }

  public toggleState(entry: Entry): void {
    let flag = entry.enabled;
    if (flag != null) {
      entry.enabled = !flag;
    } else {
      flag = entry.profile.enabled;
      entry.profile.enabled = !flag;
    }
    this.onUpdated.emit(this.cleanEntry(entry));
  }

  public updateEntry(obj: Entry) {
    this.onUpdate.emit(this.cleanEntry(obj));
  }

  public updateSelected(): void {
    const targets = this.sorted.filter(e => this.selection[e.uuid]);
    if (targets.length == 1) {
      this.updateEntry(targets[0]);
    } else if (targets.length > 1) {
      this.onUpdateMulti.emit(targets.map(this.cleanEntry));
    }
  }

  public deleteEntry(entry: Entry): Promise<any> {
    const options = {
      ...this.swalBase,
      title: 'Delete entry?',
      html: `<p>Do you want to delete <b style="font-weight: bold;">${entry.name}</b>?</p><p>You won\'t be able to revert this!</p>`
    };
    return Swal.fire(options).then(result => {
      if (result.isConfirmed) {
        this.onDelete.emit(this.cleanEntry(entry));
      }
    });
  }

  public deleteSelected(): Promise<any> {
    const sel = this.sorted.filter(e => this.selection[e.uuid]);
    const some = sel.slice(0, 3).map(e => `<b style="font-weight: bold;">${e.name}</b>`);
    const more = sel.length > some.length ? '<br/>and <b style="font-weight: bold;">' + (sel.length - some.length) + '</b> more' : '';
    const details = `<p>Do you want to delete <br/>${some.join(',<br/>')}${more}?</p>`;
    const options = {
      ...this.swalBase,
      title: `Delete ${sel.length == 1 ? 'entry' : sel.length + ' entries'}?`,
      html: details + "<p>You won't be able to revert this!</p>"
    };
    return Swal.fire(options).then(result => {
      if (result.isConfirmed) {
        const targets = this.sorted.filter(e => this.selection[e.uuid]);
        if (targets.length == 1) {
          this.onDelete.emit(this.cleanEntry(targets[0]));
        } else if (targets.length > 1) {
          this.onDeleteMulti.emit(targets.map(this.cleanEntry));
        }
        this.selectAll(false);
      }
    });
  }

  // Setup steps - Add precomputed information to entries

  private setupEntries() {
    const licences = new Set<string>();
    this.entries.forEach(entry => {
      const profile = (entry as Template)?.profile || (entry as Profile);
      const roles = this.computeRoles(profile);
      const name = (entry.username || entry.pattern) + (entry.key ? ' (' + entry.key + ')' : '');
      entry.prf = profile;
      entry.name = name;
      entry.roles = roles;
      entry.rolesTooltip = this.computeRolesTooltip(roles);
      entry.expiry = this.computeExpiry(profile, roles);
      entry.labelsText = this.computeLabels(profile);
      entry.fullname = this.computeFullname(profile);
      entry.state = this.computeState(profile, roles);
      entry.patternTooltip = this.computePatternTooltip(entry.pattern);
      // while we are on it: collect all known licences for the dropdown
      entry.prf.meta.licences?.forEach((l: Licence) => licences.add(l.key));
    });
    this.licences = Array.from(licences).sort();
    this.count = {
      total: this.entries.length,
      active: this.entries.filter(this.expiryFilters.active).length,
      expired: this.entries.filter(this.expiryFilters.expired).length,
      disabled: this.entries.filter(this.expiryFilters.disabled).length
    };
  }

  private computeFullname(profile: Profile) {
    return [profile.settings.title, profile.settings.firstname, profile.settings.lastname].filter(s => s?.length).join(' ');
  }

  private computeRoles(profile: Profile): RoleSource[] {
    let roles: string[] = [];
    const licences = profile.meta?.licences?.filter(l => l.active ?? true);
    roles.push(...(profile.meta?.roles ?? []));
    licences?.forEach(l => roles.push(...l.roles));
    roles = roles.filter((n, i) => roles.indexOf(n) === i); // remove duplicates
    return roles
      .map(role => {
        const lic = profile.meta?.roles?.includes(role) ? [] : licences?.filter(l => l.roles.includes(role));
        let exp: Date = null; // maximum expiration of all licences
        if (lic.length > 0 && lic.every(a => a.expiration)) {
          exp = lic.map(a => new Date(a.expiration as string)).reduce((a, b) => (a >= b ? a : b));
        }
        return <RoleSource>{ role, expiration: exp, licences: lic.map(l => l.key) };
      })
      .sort((a, b) => this.compareString(a.role, b.role));
  }

  private computeRolesTooltip(roles: RoleSource[]) {
    const licences = roles.flatMap(r => r.licences ?? []).filter((v, i, a) => a.indexOf(v) == i);
    switch (licences.length) {
      case 0:
        return null;
      case 1:
        return 'Licence: ' + licences[0];
      default:
        return 'Licences: ' + licences.sort().join(', ');
    }
  }

  private computeLabels(profile: Profile): string {
    return profile.labels
      ? Object.keys(profile.labels)
          .map(k => k + '=' + profile.labels[k])
          .join(', ')
      : '';
  }

  private computeState(profile: Profile, roles: RoleSource[]): State {
    if (!profile.enabled) return State.disabled;
    if (profile.meta?.expiration && new Date(profile.meta.expiration as string) < new Date()) return State.expired;
    if (roles.length === 0) return State.noperms;
    return State.active;
  }

  private computeExpiry(profile: Profile, roles: RoleSource[]): EntryExpiry {
    if (!profile?.enabled || !profile.meta) {
      return null;
    }
    if (profile.meta.duration) {
      const text = this.duration.formatIso(profile.meta.duration);
      return { text, duration: profile.meta.duration, licence: false };
    }
    if (profile.meta.expiration) {
      const date = new Date(profile.meta.expiration as string);
      const text = formatDate(date, this.dateFormat, 'en-US');
      return { text, date, licence: false };
    }
    if (roles.length > 0 && roles.every(r => r.expiration)) {
      const date = roles.map(role => role.expiration).reduce((a, b) => (a >= b ? a : b));
      const text = formatDate(date, this.dateFormat, 'en-US');
      return { text, date, licence: true };
    }
    return null;
  }

  private computePatternTooltip(pattern: string): string {
    if (pattern == null) {
      return null;
    } else if (pattern === '%') {
      return 'Allow anybody';
    } else if (pattern === '%@%.%') {
      return 'Username must be a mail address.';
    }
    const placeholders = (pattern.match(/%|_/g) || []).length;
    if (placeholders === 0) {
      return null;
    } else if (placeholders === 1) {
      const mail = pattern.match(/^%@([\w\-\.]+\.\w+)$/);
      if (mail != null) {
        return 'Username must be a mail address of domain: ' + mail[1];
      }
      const starts = pattern.match(/^(.+)%$/);
      if (starts != null) {
        return 'Username must begin with: ' + starts[1];
      }
      const ends = pattern.match(/^%(.+)$/);
      if (ends != null) {
        return 'Username must end with: ' + ends[1];
      }
    } else if (placeholders === 2) {
      const contains = pattern.match(/^%(.+)%$/);
      if (contains != null) {
        return 'Username must contain: ' + contains[1];
      }
    }

    let example = pattern;
    example = this.replaceAlternating(example, '%', 'max', 'mustermann');
    example = this.replaceAlternating(example, '_', 'x', 'y', 'z');
    return 'Username must match, e.g. ' + example;
  }

  // Label lookup for filter dropdowns

  private setupLabelLookup(): void {
    const lookup: { [key: string]: Set<string> } = {};
    this.entries
      .map(e => e.prf.labels)
      .filter(labels => labels)
      .forEach(labels =>
        Object.keys(labels).forEach(key => {
          if (!lookup[key]) lookup[key] = new Set<string>();
          lookup[key].add(labels[key]);
        })
      );
    const keys = Object.keys(lookup);
    if (keys.length === 0) {
      this.labelKeys = null;
      this.labelValues = null;
    } else {
      const sorted: { [key: string]: string[] } = {};
      keys.forEach(key => (sorted[key] = Array.from(lookup[key]).sort()));
      this.labelKeys = keys;
      this.labelValues = sorted;
    }
  }

  // Filter & Sorting

  public updateFiltered(): void {
    this.filtered = this.entries.filter(e => this.matchFilter(e));
    this.updateSorted();
  }

  private updateSorted(): void {
    if (this.sortingKey) {
      this.sorted = this.filtered.sort(this.sortingOrder[this.sortingKey]);
      if (this.sortingReverse) {
        this.sorted = this.sorted.reverse();
      }
    } else {
      this.sorted = this.filtered;
    }
  }

  private matchFilter(entry: any): boolean {
    const fr = this.filter.role;
    const fl = this.filter.licence;
    const fu = this.filter.username.toLowerCase();
    const fm = this.filter.mail.toLowerCase();
    const ex = this.expiryFilters[this.filter.expiry];
    const flk = this.filter.labelKey;
    const flv = this.filter.labelValue;
    return (
      (fr === '*' || entry.roles.some(r => r.role === fr)) &&
      (fl === '*' || entry.prf.meta.licences.some(l => l.key === fl)) &&
      (fu === '' || entry.username?.toLowerCase()?.indexOf(fu) >= 0 || entry.pattern?.indexOf(fu) >= 0) &&
      (fm === '' || (entry.prf.meta?.mail && entry.prf.meta.mail.toLowerCase().indexOf(fm) >= 0)) &&
      (flk === '*' || (entry.prf.labels[flk] && (flv === '*' || entry.prf.labels[flk] == flv))) &&
      ex(entry)
    );
  }

  public toggleSort(key: string): void {
    if (key == null) {
      this.sortingKey = null;
      this.sortingReverse = false;
    } else if (key == this.sortingKey) {
      if (!this.sortingReverse) {
        this.sortingReverse = true;
      } else {
        this.sortingKey = null;
      }
    } else {
      this.sortingKey = key;
      this.sortingReverse = false;
    }
    this.updateSorted();
  }

  // Helpers

  private compareString(a: string, b: string): number {
    if (a === b) return 0;
    else if (!a) return -1;
    else if (!b) return 1;
    else return a.toUpperCase() > b.toUpperCase() ? 1 : -1;
  }

  private compareRoles(a: Profile, b: Profile): number {
    const ra = a.meta.roles;
    const rb = b.meta.roles;
    const len = Math.min(ra?.length ?? 0, rb?.length ?? 0);
    for (let i = 0; i < len; i++) {
      const c = this.compareString(ra[i], rb[i]);
      if (c != 0) return c;
    }
    return rb.length - ra.length;
  }

  private compareExpiry(a: Entry, b: Entry): number {
    let c = b.state - a.state;
    if (c === 0) c = this.compareDate(a.expiry?.date, b.expiry?.date);
    if (c === 0) c = this.compareDuration(a.expiry?.duration, b.expiry?.duration);
    return c;
  }

  private compareDate(a: Date, b: Date) {
    if (a === b) return 0;
    else if (!a) return 1;
    else if (!b) return -1;
    else return a > b ? 1 : -1;
  }

  private compareDuration(a: Duration, b: Duration) {
    if (a === b) return 0;
    else if (!a) return 1;
    else if (!b) return -1;
    else return this.duration.toDuration(a) > this.duration.toDuration(b) ? 1 : -1;
  }

  private replaceAlternating(text: string, search: string, ...values: string[]): string {
    let repl = 0;
    while (text.indexOf(search) >= 0) {
      text = text.replace(search, values[repl++ % values.length]);
    }
    return text;
  }

  private cleanEntry(obj: Entry): Profile | Template {
    // undo setup steps
    // remove precomputed information from entries to send them to output
    const { prf, name, fullname, roles, rolesTooltip, labelsText, state, expiry, patternTooltip, ...data } = obj;
    return data;
  }
}
